import * as React from 'react';
import { useEffect, useRef, useState } from 'react';
import Box from '@mui/material/Box';
import List from '@mui/material/List';
import CssBaseline from '@mui/material/CssBaseline';
import IconButton from '@mui/material/IconButton';
import { Outlet } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import NavbarWithSubMenu from './navbarWithSubMenu';
import BasicNavbar from './basicNavbar';
import Header from '../header/header';
import DigtectiveLogo from '../../assets/images/logos/digtective-logo_primary.svg';
import CloseSidebar from '../../assets/images/logos/closeSidebar.svg';
import DigtectiveLogoSmall from '../../assets/images/logos/digtective-logo-small_primary.svg';
import { Drawer, DrawerHeader } from './sidebarMixins';
import LoadingSpinner from '../loadingSpinner/loadingSpinner';
import useGetOrganizationSettings from '../../apiHooks/queries/useGetOrganizations';
import { ORGANIZATION_SETTINGS } from '../../constants/pageIdentifiers';
import DashboardIconPrimary from '../../assets/icons/sidebar/dashboard-icon-primary.svg';
import DashboardIconWhite from '../../assets/icons/sidebar/dashboard-icon-white.svg';
import AdministrationIconPrimary from '../../assets/icons/sidebar/administration-icon-primary.svg';
import AdministrationIconWhite from '../../assets/icons/sidebar/administration-icon-white.svg';
import DevelopersIconPrimary from '../../assets/icons/sidebar/developers-icon-primary.svg';
import DevelopersIconWhite from '../../assets/icons/sidebar/developers-icon-white.svg';
import AdManagementIconPrimary from '../../assets/icons/sidebar/ad-management-icon-primary.svg';
import AdManagementIconWhite from '../../assets/icons/sidebar/ad-management-icon-white.svg';
import SettingsIconPrimary from '../../assets/icons/sidebar/settings-icon-primary.svg';
import SettingsIconWhite from '../../assets/icons/sidebar/settings-icon-white.svg';
import UsersIconWhite from '../../assets/icons/sidebar/submenu/users-icon-white.svg';
import ConsoleIconWhite from '../../assets/icons/sidebar/submenu/console-icon-white.svg';
import APIDocsIconWhite from '../../assets/icons/sidebar/submenu/api-docs-icon-white.svg';
import AdsIconWhite from '../../assets/icons/sidebar/submenu/ads-icon-white.svg';
import IntegrationsListIconWhite from '../../assets/icons/sidebar/submenu/integrations-list-icon-white.svg';
import PaymentProviderIconWhite from '../../assets/icons/sidebar/submenu/payment-provider-icon-white.svg';
import AdGroupsIconWhite from '../../assets/icons/sidebar/submenu/ad-groups-white.svg';
import CampaignsIconWhite from '../../assets/icons/sidebar/submenu/campaigns-icon-white.svg';
import IntegrationsIconPrimary from '../../assets/icons/sidebar/integrations-icon-primary.svg';
import IntegrationsIconWhite from '../../assets/icons/sidebar/integrations-icon-white.svg';
import SubscriptionIconWhite from '../../assets/icons/sidebar/subscription-icon-white.svg';
import CustomFieldsIconWhite from '../../assets/icons/sidebar/submenu/custom-fields-icon-white.svg';
import OrgSettingsIconWhite from '../../assets/icons/sidebar/submenu/org-settings-icon-white.svg';
import EcommerceIcon from '../../assets/icons/sidebar/e-commerce-icon.svg';
import GoogleConversionsIcon from '../../assets/icons/googleConversionIcon.svg';
import TrackingCodeIcon from '../../assets/icons/sidebar/tackingCodeIconSidebar.svg';
import TrackingIconWhite from '../../assets/icons/sidebar/trackingIconSidebar.svg';
import TrackingIcon from '../../assets/icons/sidebar/tackingIconSidebarPrimary.svg';
import TrackingSourceNewIcon from '../../assets/icons/sidebar/newTrackingSourceIcon.svg';
import SidebarQuestionMark from '../../assets/icons/sidebar/sidebar_questionmark.svg';
import ImportIcon from '../../assets/icons/sidebar/import_icon.svg';
import { useOrganizationSettingsStore } from '../../store/organizationSettingsStore';
import { useSidebarStore } from '../../store/sidebarStore';
import { OrganizationSettingsInterface } from '../../interfaces/organizationSettings.interface';
import { DiggerPlanEnum } from '../enums/DiggerPlanEnum';
import Breadcrumb from '../breadcrumb/breadcrumb';
import ConfirmDialog from '../../dialogs/confirmDialog';
import GoogleConversionsDialog from '../../dialogs/googleConversionsDialog';
import { useSendAcceptManage } from '../../apiHooks/mutations/acceptManagement/sendAcceptManagment';
import { useSendDeclineManage } from '../../apiHooks/mutations/declineManagement/sendDeclineManagment';
import { ManagerialStatus } from
  '../../pages/administration/organizationSettings/organizationSettingsPanels/managerialAccountPanel';
import AccountMenu from '../accountMenu/accountMenu';
import { useSwitchOrganizations } from '../../apiHooks/mutations/switchOrganizations/useSwitchOrganizations';
import useGetManagedOrganizationsNames
  from '../../apiHooks/queries/organizationManagedNames/getOrganizationsManagedNames';
import OrganizationDropdownButton from '../organizationDropdown/organizationDropdown';
import OrganizationDropdownList from '../organizationDropdownList/organizationDropdownList';
import EmailAtIcon from '../../assets/icons/email-at-icon-gray.svg';
import CrmIcon from '../../assets/icons/crm-logo.svg';
import analyticsIcon from '../../assets/icons/sidebar/analyticsIcon.png';
import OrganizationType from '../enums/organizationTypesEnum';

const Sidebar = () => {
  const isSidebarOpen = useSidebarStore((state) => state.isSidebarOpen);
  const setIsSidebarOpen = useSidebarStore((state) => state.setIsSidebarOpen);
  const [openedSubMenu, setOpenedSubMenu] = useState('');
  const organizationSettings = useOrganizationSettingsStore((state) => state.organizationSettings);
  const setOrganizationSettings = useOrganizationSettingsStore((state) => state.setOrganizationSettings);
  const { t } = useTranslation();
  const [openModal, setOpenModal] = useState<boolean>(false);
  const [urlToken, setUrlToken] = useState<string>('');
  const [isOpen, setIsOpen] = useState(false);
  const { data: organizations } = useGetManagedOrganizationsNames('MANAGED_ORGANIZATIONS_NAMES');
  const { data: organizationsSettings } = useGetOrganizationSettings('');
  const { mutate: switchOrganization } = useSwitchOrganizations();
  const managerOrgName = organizationsSettings?.data?.data?.managerOrganizationName;

  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const dropdownRef = useRef<HTMLDivElement | null>(null);

  const handleToggle = () => {
    setIsOpen(!isOpen);
  };

  const toggleSidebar = () => {
    setIsSidebarOpen((prev) => !prev);
  };

  const { mutate: sendAccept } = useSendAcceptManage();
  const { mutate: sendDecline } = useSendDeclineManage();

  const closeAndDisconnect = () => {
    sendDecline(urlToken);
    setOpenModal(false);
  };

  const closeAndAccept = () => {
    sendAccept(urlToken);
    setOpenModal(false);
  };

  const [showConversionsDialog, setShowConversionsDialog] = useState<boolean>(false);

  const maybeOpenConversionsDialog = (organizationSettingsRetrieved: OrganizationSettingsInterface) => {
    const hasOrganizationType = !!organizationSettingsRetrieved.organizationType;
    const hasSeenDialog = localStorage.getItem('hasSeenChooseOrganizationPopup') === 'true';

    if (!hasOrganizationType && !hasSeenDialog) {
      setShowConversionsDialog(true);
      localStorage.setItem('hasSeenChooseOrganizationPopup', 'true');
    }
  };

  const onOrganizationSettingsRetrieved = (organizationSettingsRetrieved: OrganizationSettingsInterface) => {
    setOrganizationSettings(organizationSettingsRetrieved);
    if (organizationSettingsRetrieved.manageToken) {
      setUrlToken(organizationSettingsRetrieved.manageToken);
      setOpenModal(true);
    }

    // Show once per browser, right after first successful login if org type isn't set yet
    maybeOpenConversionsDialog(organizationSettingsRetrieved);
  };

  const {
    isLoading: isLoadingOrganizationSettings,
    isError: isErrorOrganizationSettings,
  } = useGetOrganizationSettings(ORGANIZATION_SETTINGS, onOrganizationSettingsRetrieved);

  const handleSubMenuClick = (href: string) => {
    if (openedSubMenu !== href) {
      setOpenedSubMenu(href);
    } else {
      setOpenedSubMenu('');
    }
  };

  const toggleDrawerOpen = () => {
    setIsSidebarOpen(!isSidebarOpen);
    if (isSidebarOpen) {
      setOpenedSubMenu('');

    }
  };

  const handleHelpAndSupportClick = () => {
    const knowledgeBaseUrl = 'https://knowledge.digtective.com';
    const newTab = window.open(knowledgeBaseUrl, '_blank', 'noopener,noreferrer');
    if (!newTab) window.location.href = knowledgeBaseUrl;
  };

  const [isHovered, setIsHovered] = useState(false);

  useEffect(() => {
    const sidebar = document.getElementById('sidebar');
    if (!sidebar) return;
  }, []);

  const closeDropdown = (e: MouseEvent) => {
    if (
      buttonRef.current && buttonRef.current.contains(e.target as Node)
        || dropdownRef.current && dropdownRef.current.contains(e.target as Node)
    ) {
      return;
    }
    setIsOpen(false);
  };

  useEffect(() => {
    setIsSidebarOpen(true);
    localStorage.setItem('isSidebarOpen', JSON.stringify(true));
  }, [setIsSidebarOpen]);

  if (isLoadingOrganizationSettings) (<LoadingSpinner center color="primary" />);
  if (isErrorOrganizationSettings) (<div>{t('somethingWentWrong')}</div>);

  const navigationList = [
    {
      label: 'dashboard',
      iconPrimary: DashboardIconPrimary,
      iconSecondary: DashboardIconWhite,
      href: '/dashboard',
    }, {
      label: 'administration',
      iconPrimary: AdministrationIconPrimary,
      iconSecondary: AdministrationIconWhite,
      href: '/administration',
      isOpen: openedSubMenu === '/administration',
      subList: [{
        label: 'users',
        iconWhite: UsersIconWhite,
        href: 'users',
      }],
    }, {
      label: 'adManagement',
      iconPrimary: AdManagementIconPrimary,
      iconSecondary: AdManagementIconWhite,
      href: '/ad-management',
      isOpen: openedSubMenu === '/ad-management',
      subList: [{
        label: 'trackingSources',
        iconWhite: TrackingSourceNewIcon,
        href: 'tracking-sources',
      },
      {
        label: 'campaigns',
        iconWhite: CampaignsIconWhite,
        href: 'campaigns',
      }, {
        label: 'adGroups',
        iconWhite: AdGroupsIconWhite,
        href: 'ad-groups',
      }, {
        label: 'ads',
        iconWhite: AdsIconWhite,
        href: 'ads',
      },
      {
        label: 'import-ads',
        iconWhite: ImportIcon,
        href: 'import-ads',
      },
      ],
    },
    {
      label: 'tracking',
      iconPrimary: TrackingIcon,
      iconSecondary: TrackingIconWhite,
      href: '/tracking',
      isOpen: openedSubMenu === '/tracking',
      subList: [
        {
          label: 'tracking-code',
          iconWhite: TrackingCodeIcon,
          href: 'tracking-code',
        },
        {
          label: 'conversionActions',
          iconWhite: GoogleConversionsIcon,
          href: !organizationSettings.organizationType ? 'google-conversions' : 'google-conversions/conversions',
        },
      ],
    },
    {
      label: 'integrations',
      iconPrimary: IntegrationsIconPrimary,
      iconSecondary: IntegrationsIconWhite,
      href: '/integrations',
      isOpen: openedSubMenu === '/integrations',
      subList: [{
        label: 'CRM',
        iconWhite: CrmIcon,
        href: 'crm/hubspot',
        isLocked:
            organizationSettings.productId && (
              organizationSettings.productId === DiggerPlanEnum.lite
                || organizationSettings.productId === DiggerPlanEnum.standard
            ),
      },
      {
        label: 'ad-providers',
        iconWhite: IntegrationsListIconWhite,
        href: 'ad-providers/google',
      },
      {
        label: 'email-providers',
        iconWhite: EmailAtIcon,
        href: 'email-providers/mailchimp',
      },
      {
        label: 'payment-provider',
        iconWhite: PaymentProviderIconWhite,
        href: 'payment-provider/paddle',
      },
      {
        label: 'e-commerce',
        iconWhite: EcommerceIcon,
        href: 'e-commerce/shopify',
        hide: !(
          organizationSettings.shopifyEnabled
          || organizationSettings.organizationType === OrganizationType.ECOMMERCE
        ),
      },
      {
        label: 'analytics',
        iconWhite: analyticsIcon,
        href: 'analytics/google-analytics',
      },
      ],
    },

    {
      label: 'settings',
      iconPrimary: SettingsIconPrimary,
      iconSecondary: SettingsIconWhite,
      href: '/settings',
      isOpen: openedSubMenu === '/settings',
      subList: [
        {
          label: 'customFields',
          iconWhite: CustomFieldsIconWhite,
          href: 'custom-fields',
        },
        {
          label: 'organizationSettings',
          iconWhite: OrgSettingsIconWhite,
          href: organizationSettings.managerialStatus === ManagerialStatus.IN_ORGANIZATION
            ? 'organization-settings/calculations' : 'organization-settings/managerialAccount',
        },
        {
          label: 'subscription',
          iconWhite: SubscriptionIconWhite,
          href: 'subscription',
        }],
    },
    {
      label: 'developers',
      iconPrimary: DevelopersIconPrimary,
      iconSecondary: DevelopersIconWhite,
      href: '/developers',
      isOpen: openedSubMenu === '/developers',
      subList: [{
        label: 'console',
        iconWhite: ConsoleIconWhite,
        href: 'console',
      }, {
        label: 'api-docs',
        iconWhite: APIDocsIconWhite,
        href: 'api-docs',
      }],
    },
    // {
    //   label: 'Form Submission',
    //   iconPrimary: APIDocsIconWhite,
    //   iconSecondary: APIDocsIconWhite,
    //   href: '/form-submission',
    //   isOpen: openedSubMenu === '/form-submission',
    // },
  ];

  return (
    <>
      <Box data-cy="sidebar" className="flex">
        <CssBaseline />
        <Header open={isSidebarOpen} />
        <Drawer
          variant="permanent"
          id="sidebar"
          open={isSidebarOpen}
          className="z-[9999999999999999]"
          elevation={100}
          sx={{
            display: 'flex',
            flexDirection: 'column',
            height: '100vh',
            boxShadow: '0px 0px 0px 0px rgba(0, 0, 0, 0.00)',
          }}
        >
          <div className="flex h-full w-full flex-col">
            <DrawerHeader className="bg-primary" style={{ height: 80, padding: 0 }}>
              <IconButton
                onClick={toggleDrawerOpen}
                disableRipple
                onMouseEnter={() => !isSidebarOpen && setIsHovered(true)}
                onMouseLeave={() => !isSidebarOpen && setIsHovered(false)}
                style={{
                  transition: '200ms',
                  marginBottom: '12px',
                  width: isSidebarOpen ? '230px' : '100%',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                }}
              >
                {isSidebarOpen ? (
                  <div className="flex flex-row gap-[10px] p-0">
                    <img
                      src={DigtectiveLogo}
                      alt="Digtective Logo"
                      className="relative right-[15px] max-h-[65px] w-full max-w-[220px] object-cover"
                    />
                    <img
                      src={CloseSidebar}
                      alt="Close"
                      className="mt-[26px] max-h-[16px] w-full max-w-[16px] object-cover duration-200"
                      onClick={toggleSidebar}
                    />
                  </div>
                ) : (
                  <img
                    src={isHovered ? CloseSidebar : DigtectiveLogoSmall}
                    alt="Collapsed Logo"
                    className="max-h-[50px] object-contain"
                    style={{
                      transform: isHovered ? 'rotateY(180deg)' : 'rotateY(0deg)',
                      transition: 'transform 0.6s ease-in-out',
                    }}
                  />
                )}
              </IconButton>
            </DrawerHeader>

            <div style={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
              <List sx={{ width: '100%' }}>
                {navigationList.map((item) => {
                  const hasSubList = !!item.subList;
                  return (
                    <div key={item.label} className="w-full text-[14px] font-thin leading-[18px]">
                      {hasSubList ? (
                        <NavbarWithSubMenu
                          item={item}
                          setOpenedSubMenu={setOpenedSubMenu}
                          handleSubMenuClick={handleSubMenuClick}
                        />
                      ) : (
                        <BasicNavbar
                          item={item}
                          handleSubMenuClick={handleSubMenuClick}
                        />
                      )}
                    </div>
                  );
                })}
              </List>
            </div>

            <div className="mb-4 mt-auto flex w-full flex-col items-center justify-center">
              <button
                type="button"
                className={`mb-4 mt-2 flex ${
                  isSidebarOpen ? 'w-[calc(100%-25px)] gap-4' : 'w-[calc(100%-15px)]'
                } cursor-pointer items-center justify-center border-b border-b-white pb-4`}
                onClick={handleHelpAndSupportClick}
                style={{ background: 'transparent', border: 'none', padding: 0 }}
              >
                <img className="h-4 w-4" src={SidebarQuestionMark} alt="settings" />
                {isSidebarOpen && <div className="text-xs text-white">{t('helpAndSupport')}</div>}
              </button>
              <div
                onClick={handleToggle}
                className={`${
                  isSidebarOpen
                    ? 'justify-start rounded-lg border border-[#fff] px-2 py-1 hover:bg-[#3d6d7e]'
                    : 'justify-center'
                } flex h-[54px] w-full max-w-[200px] items-center gap-3`}
              >
                {isSidebarOpen ? null : <AccountMenu />}
                {isSidebarOpen && (
                  <OrganizationDropdownButton
                    organizations={organizations}
                    ref={buttonRef}
                    handleToggle={handleToggle}
                    isOpen={isOpen}
                  />
                )}
              </div>
            </div>
          </div>
        </Drawer>

        <Box
          component="main"
          sx={{
            flexGrow: 1,
            p: 3,
            backgroundColor: '#F5F5F5',
            minHeight: '100vh',
            overflowX: 'hidden',
            maxWidth: '100%',
          }}
        >
          <DrawerHeader />
          <div className="h-[calc(100%-80px)] max-w-[1920px] duration-200">
            <Breadcrumb />
            {organizationSettings && <Outlet />}
            <OrganizationDropdownList
              ref={dropdownRef}
              isOpen={isOpen}
              setIsOpen={setIsOpen}
              organizations={organizations}
              switchOrganization={switchOrganization}
            />
          </div>
        </Box>
      </Box>
        
      <ConfirmDialog
        variant="warning"
        onCloseDialog={() => closeAndDisconnect()}
        title="Managerial Organization"
        text={`Do you want ${managerOrgName} to manage your account?`}
        submitButtonText="accept"
        onSubmit={() => closeAndAccept()}
        open={openModal}
      />

      <GoogleConversionsDialog
        open={showConversionsDialog}
        onClose={() => setShowConversionsDialog(false)}
      />
    </>
  );
};

export default Sidebar;
